// components/AsyncSelector/AsyncSelector.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react'
import AsyncSelect from 'react-select/async'
import { toast } from 'react-toastify'
import api from 'api/api'
import HighlightableText from 'components/HighlightableText'

/**
 * Selector genérico con búsqueda asíncrona
 */
const AsyncSelector = ({
	endpoint,
	searchFields = [],
	valueField = 'id',
	labelFormatter = (item) => item.name || item.nombre || `Item ${item.id}`,
	optionFormatter = null,
	placeholder = 'Buscar...',
	value = null,
	onChange = () => { },
	disabled = false,
	isClearable = true,
	isMulti = false,
	additionalFilters = {},
	minSearchLength = 2,
	debounceMs = 500,
	maxResults = 50,
	noOptionsMessage = 'No se encontraron resultados',
	loadingMessage = 'Buscando...',
	customStyles = {},
	...otherProps
}) => {
	const [inputValue, setInputValue] = useState('')
	const debounceRef = useRef(null)

	// Función de búsqueda simplificada
	const searchAPI = useCallback(async (searchTerm) => {
		try {
			if (!searchTerm || searchTerm.length < minSearchLength) {
				return []
			}

			const filtros = { ...additionalFilters }

			searchFields.forEach(field => {
				filtros[field] = [searchTerm]
			})

			const response = await api.post(endpoint, {
				cuantos: maxResults,
				desde: 0,
				filtros
			})

			const datos = response.datos || response
			let items = []

			if (datos.usuarios) items = datos.usuarios
			else if (datos.items) items = datos.items
			else if (datos.productos) items = datos.productos
			else if (Array.isArray(datos)) items = datos
			else items = []

			return items.map(item => ({
				value: item[valueField],
				label: labelFormatter(item),
				data: item,
				searchTerm
			}))

		} catch (error) {
			console.error('Error en búsqueda:', error)
			toast.error('Error al buscar')
			return []
		}
	}, [endpoint, searchFields, additionalFilters, maxResults, minSearchLength, labelFormatter, valueField])

	// LoadOptions con debounce manual robusto
	const loadOptions = useCallback((inputValue, callback) => {
		// Limpiar timeout anterior
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
		}

		if (!inputValue || inputValue.length < minSearchLength) {
			callback([])
			return
		}

		debounceRef.current = setTimeout(() => {
			searchAPI(inputValue)
				.then(options => callback(options))
				.catch(() => callback([]))
		}, debounceMs)
	}, [searchAPI, minSearchLength, debounceMs])

	// Cleanup
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
			}
		}
	}, [])

	// Formatear opciones
	const formatOptionLabel = useCallback((option, { context }) => {
		if (!option) return null

		if (optionFormatter) {
			return optionFormatter(option, context)
		}

		return (
			<HighlightableText
				text={option.label}
				highlight={option.searchTerm || inputValue}
			/>
		)
	}, [optionFormatter, inputValue])

	// Estilos optimizados
	const selectStyles = {
		control: (provided, state) => ({
			...provided,
			minHeight: '38px',
			borderColor: state.isFocused ? '#80bdff' : '#ced4da',
			boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none'
		}),
		option: (provided, state) => ({
			...provided,
			backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
			color: '#212529',
			cursor: 'pointer'
		}),
		menu: (provided) => ({
			...provided,
			zIndex: 9999,
			maxHeight: '200px'
		}),
		menuList: (provided) => ({
			...provided,
			maxHeight: '200px'
		}),
		...customStyles
	}

	// Valor actual
	const currentValue = value ? {
		value: value[valueField],
		label: labelFormatter(value),
		data: value
	} : null

	return (
		<div style={{ position: 'relative' }}>
			<AsyncSelect
				cacheOptions={false}
				loadOptions={loadOptions}
				value={currentValue}
				onChange={(selectedOption) => {
					if (isMulti) {
						const selectedData = selectedOption?.map(opt => opt.data) || []
						onChange(selectedData)
					} else {
						onChange(selectedOption?.data || null)
					}
				}}
				onInputChange={(newValue, actionMeta) => {
					if (actionMeta.action === 'input-change') {
						setInputValue(newValue)
					}
				}}
				formatOptionLabel={formatOptionLabel}
				placeholder={placeholder}
				isDisabled={disabled}
				isClearable={isClearable}
				isMulti={isMulti}
				loadingMessage={() => loadingMessage}
				noOptionsMessage={() => noOptionsMessage}
				styles={selectStyles}
				menuPortalTarget={null}
				{...otherProps}
			/>
		</div>
	)
}

export default AsyncSelector